import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area, AreaChart,
  Bar, BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Scatter, ScatterChart,
  Tooltip,
  XAxis, YAxis, ZAxis,
} from 'recharts';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import {
  s,
  authHeaders,
  buildODataUrl,
  Toast,
  newRecordButtonStyle,
  getODataRows,
  getExplicitODataTotalCount,
  resolveODataTotalCount,
  toGridSortField,
  toODataSortField,
  ExcelIcon,
  exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, AvatarBadge, StatusBadge, PillTag } from './ui';
import {
  buildFilterClause, unwrapLookupItems,
  ColumnFilterPopover, ColumnChooser, MultiGroupPicker, ViewSwitch, DEFAULT_VIEW_MODES, C360CardGrid,
  DonutCard, fmtTRY,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { CustomerGroupsPage } from './CustomerGroupsPage';
import { CustomerSubGroupsPage } from './CustomerSubGroupsPage';
import './CustomersPage.css';

const API_BASE = '/api/customers';

// Ürünler ekranındaki tab desenine paralel: Müşteriler / Grup / Alt Grup sekmeleri.
// Sekmeler artık rota tabanlı: /customer-groups ve /customer-sub-groups da CustomersPage'i
// render eder (`definition` prop'u ile), böylece kayıt sonrası listeye dönüşte sekmeler kaybolmaz.
const definitionTabs = [
  { key: 'customers', label: 'Müşteriler', route: '/customers' },
  { key: 'groups', label: 'Müşteri Grubu', route: '/customer-groups' },
  { key: 'subGroups', label: 'Müşteri Alt Grubu', route: '/customer-sub-groups' },
];

const customerDefinitionStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: {
    minHeight: 36, padding: '8px 14px', borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700,
  },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};
// Liste/Kanban/Grafik + prototip Kart görünümü (sfa-tasarim-ornek.html .cardgrid/.ccard).
// Kart, Liste'nin hemen YANINDA (index 1); geri kalan modlar korunur.
const CUSTOMER_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

const STATUS_META = {
  0: { label: 'Aktif', tone: 'ok' },
  1: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
  // 3 = Potansiyel: "İşlem Görmeyen Müşteriler" zamanlanmış görevinin (menü 9608) yazdığı durum.
  // Müşteri PASİF DEĞİLDİR — rutta kalır ve hareket görünce otomatik eski durumuna döner.
  3: { label: 'Potansiyel', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));
// #129 E-Belge Mükellef Tipi kodu → etiket (dışa aktarma için).
const EDOCUMENT_TYPE_LABELS = {
  EINVOICE: 'E-Fatura',
  EINVOICE_EDISPATCH: 'E-Fatura + E-İrsaliye',
  EARCHIVE: 'E-Arşiv',
};
const GROUP_OPTIONS = [
  { value: 'status', label: 'Durum' },
  { value: 'groupId', label: 'Müşteri Grubu' },
  { value: 'subGroupId', label: 'Ek Grup' },
  { value: 'vendorId', label: 'Distribütör' },
  { value: 'city', label: 'Şehir' },
];

// Group-by field → OData field/type (bucket-specific count query'leri için)
const GROUP_BY_ODATA = {
  status: { field: 'Status', type: 'number' },
  groupId: { field: 'GroupId', type: 'guid' },
  subGroupId: { field: 'SubGroupId', type: 'guid' },
  vendorId: { field: 'VendorId', type: 'guid' },
  city: { field: 'City', type: 'string' },
};
const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  title: { type: 'text', odataField: 'Title' },
  shortName: { type: 'text', odataField: 'ShortName' },
  person: { type: 'text', odataField: 'Person' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
  groupLabel: { type: 'select', odataField: 'GroupId', valueFormat: 'guid' },
  subGroupLabel: { type: 'select', odataField: 'SubGroupId', valueFormat: 'guid' },
  vendorLabel: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
};

/**
 * Satırların `currentBalance` alanını HESAPLANMIŞ bakiyeyle doldurur.
 *
 * ⚠ `Customer.CurrentBalance` KOLONU ARTIK YAZILMIYOR (CARI_BAKIYE_DUZELTME.md B1): bakiye
 * saklanmıyor, `Invoice` + `FinanceTransaction` toplamından hesaplanıyor. OData listesi ham
 * kolonu döndürdüğü için bayat gelir; burada tek bir toplu çağrıyla (§8 N+1 önlemi) doğrusuyla
 * değiştirilir. Alan ADI korunur — ekranların geri kalanı değişmez.
 *
 * Bakiye ucu hata verirse satırlar OLDUĞU GİBİ bırakılır: liste bakiye yüzünden boş kalmasın.
 */
async function withComputedBalances(rows) {
  const ids = Array.from(new Set(rows.map((r) => r.id || r.Id).filter(Boolean)));
  if (ids.length === 0) return rows;
  try {
    const res = await fetch(`${API_BASE}/balances`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids }),
    });
    if (!res.ok) return rows;
    const json = await res.json();
    const items = (json?.data ?? json?.Data ?? json)?.items
      ?? (json?.data ?? json?.Data ?? json)?.Items ?? [];
    const map = {};
    items.forEach((b) => { map[b.customerId ?? b.CustomerId] = b.balance ?? b.Balance; });
    return rows.map((r) => {
      const id = r.id || r.Id;
      return id in map ? { ...r, currentBalance: map[id] } : r;
    });
  } catch {
    return rows;
  }
}

function ChartView({ rows }) {
  const total = rows.length;

  // === KPI hesaplamaları ===
  const active = rows.filter((r) => Number(r.status) === 0).length;
  const totalBalance = rows.reduce((sum, r) => sum + Number(r.currentBalance || 0), 0);
  const limitVals = rows.map((r) => Number(r.creditLimit1 || 0)).filter((v) => v > 0);
  const avgLimit = limitVals.length > 0 ? limitVals.reduce((a, b) => a + b, 0) / limitVals.length : 0;
  const eInvoiceCount = rows.filter((r) => r.isEinvoice).length;
  const eDispatchCount = rows.filter((r) => r.isEdispatch).length;
  const eInvoicePct = total > 0 ? (eInvoiceCount / total) * 100 : 0;
  const eDispatchPct = total > 0 ? (eDispatchCount / total) * 100 : 0;
  const now = new Date();
  const ymCurrent = now.getFullYear() * 100 + (now.getMonth() + 1);
  const newThisMonth = rows.filter((r) => {
    if (!r.createdDate) return false;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() * 100 + (d.getMonth() + 1) === ymCurrent;
  }).length;

  // === Bakiye histogramı ===
  const balanceBuckets = [
    { label: '0', min: -Infinity, max: 0 },
    { label: '0-1K', min: 0, max: 1000 },
    { label: '1K-10K', min: 1000, max: 10000 },
    { label: '10K-50K', min: 10000, max: 50000 },
    { label: '50K-100K', min: 50000, max: 100000 },
    { label: '100K+', min: 100000, max: Infinity },
  ].map((b) => ({ ...b, count: 0 }));
  rows.forEach((r) => {
    const v = Number(r.currentBalance || 0);
    for (const b of balanceBuckets) {
      if (v > b.min && v <= b.max) { b.count += 1; break; }
      if (v === 0 && b.min === -Infinity) { b.count += 1; break; }
    }
  });
  const balanceData = balanceBuckets.map((b) => ({ name: b.label, value: b.count }));

  // === Limit vs Bakiye scatter ===
  const scatterByStatus = { 0: [], 1: [], 2: [], other: [] };
  rows.forEach((r) => {
    const x = Number(r.creditLimit1 || 0);
    const y = Number(r.currentBalance || 0);
    if (x === 0 && y === 0) return;
    const point = { x, y, name: r.title || r.textId };
    const s = Number(r.status);
    if (s === 0) scatterByStatus[0].push(point);
    else if (s === 1) scatterByStatus[1].push(point);
    else if (s === 2) scatterByStatus[2].push(point);
    else scatterByStatus.other.push(point);
  });

  // === Müşteri Kazanım Trendi (son 12 ay) ===
  const trendOrder = [];
  const trendMap = {};
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
    trendMap[key] = { label, count: 0 };
    trendOrder.push(key);
  }
  rows.forEach((r) => {
    if (!r.createdDate) return;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (trendMap[key]) trendMap[key].count += 1;
  });
  const trendData = trendOrder.map((k) => trendMap[k]);

  // === E-Fatura / E-İrsaliye donuts ===
  const eInvoiceData = [
    { name: 'E-Fatura', value: eInvoiceCount, color: '#00c875' },
    { name: 'Klasik', value: Math.max(0, total - eInvoiceCount), color: '#cbd5e1' },
  ];
  const eDispatchData = [
    { name: 'E-İrsaliye', value: eDispatchCount, color: '#0ea5e9' },
    { name: 'Klasik', value: Math.max(0, total - eDispatchCount), color: '#cbd5e1' },
  ];

  // === Vergi Tipi pie ===
  const taxLabels = { 0: 'Gerçek Kişi', 1: 'Tüzel Kişi', 2: 'Kamu Kurumu' };
  const taxCounts = {};
  rows.forEach((r) => {
    const t = r.taxType == null ? 'Bilinmiyor' : String(r.taxType);
    taxCounts[t] = (taxCounts[t] || 0) + 1;
  });
  const taxPalette = ['#0073ea', '#00c875', '#a855f7', '#fdab3d', '#94a3b8'];
  const taxData = Object.entries(taxCounts).map(([k, v], i) => ({
    name: taxLabels[k] || (k === 'Bilinmiyor' ? 'Bilinmiyor' : `Tip ${k}`),
    value: v,
    color: taxPalette[i % taxPalette.length],
  }));

  return (
    <div className="c360-dashboard">
      {/* KPI şeridi — prototip stat kartları (renkli üst kenar + büyük değer + alt not) */}
      <div className="cust-stat">
        <div className="cust-scard" style={{ borderTopColor: 'var(--m-navy, #323d6b)' }}>
          <div className="cust-scard-l">Toplam Müşteri</div>
          <div className="cust-scard-v">{total.toLocaleString('tr-TR')}</div>
          <div className="cust-scard-m">Kayıtlı cari</div>
        </div>
        <div className="cust-scard" style={{ borderTopColor: 'var(--success, #00c875)' }}>
          <div className="cust-scard-l">Aktif Müşteri</div>
          <div className="cust-scard-v" style={{ color: 'var(--success, #00c875)' }}>{active.toLocaleString('tr-TR')}</div>
          <div className="cust-scard-m">{total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'}</div>
        </div>
        <div className="cust-scard" style={{ borderTopColor: 'var(--danger, #e2445c)' }}>
          <div className="cust-scard-l">Toplam Açık Bakiye</div>
          <div className="cust-scard-v" style={{ color: 'var(--danger, #e2445c)' }}>{fmtTRY(totalBalance)}</div>
          <div className="cust-scard-m">{total.toLocaleString('tr-TR')} cari</div>
        </div>
        <div className="cust-scard" style={{ borderTopColor: 'var(--accent, #0073ea)' }}>
          <div className="cust-scard-l">Ort. Kredi Limiti</div>
          <div className="cust-scard-v">{fmtTRY(avgLimit)}</div>
          <div className="cust-scard-m">{limitVals.length} limit kayıtlı</div>
        </div>
        <div className="cust-scard" style={{ borderTopColor: 'var(--m-purple, #a25ddc)' }}>
          <div className="cust-scard-l">E-Fatura</div>
          <div className="cust-scard-v" style={{ color: 'var(--m-purple, #a25ddc)' }}>%{eInvoicePct.toFixed(0)}</div>
          <div className="cust-scard-m">{eInvoiceCount} / {total}</div>
        </div>
        <div className="cust-scard" style={{ borderTopColor: 'var(--warn, #fdab3d)' }}>
          <div className="cust-scard-l">Bu Ay Eklenen</div>
          <div className="cust-scard-v" style={{ color: 'var(--warn, #fdab3d)' }}>+{newThisMonth.toLocaleString('tr-TR')}</div>
          <div className="cust-scard-m">Yeni müşteri</div>
        </div>
      </div>

      {/* Bakiye histogram + Scatter risk haritası */}
      <div className="c360-chart-grid">
        <div className="c360-chart-panel">
          <div className="c360-chart-panel-head">
            <span className="c360-chart-panel-title">Bakiye Dağılımı</span>
            <span className="c360-chart-panel-sub">Toplam {fmtTRY(totalBalance)}</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={balanceData} margin={{ top: 12, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} müşteri`, 'Adet']} />
              <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
                <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="c360-chart-panel">
          <div className="c360-chart-panel-head">
            <span className="c360-chart-panel-title">Kredi Limit ↔ Açık Bakiye</span>
            <span className="c360-chart-panel-sub">Risk haritası</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 12, right: 16, left: 4, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" dataKey="x" name="Kredi Limiti" stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
              <YAxis type="number" dataKey="y" name="Bakiye" stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
              <ZAxis range={[40, 40]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, name) => [name === 'x' || name === 'y' ? fmtTRY(v) : v, name === 'x' ? 'Limit' : 'Bakiye']} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Scatter name="Aktif" data={scatterByStatus[0]} fill="#00c875" />
              <Scatter name="Pasif" data={scatterByStatus[1]} fill="#94a3b8" />
              <Scatter name="Beklemede" data={scatterByStatus[2]} fill="#fdab3d" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Trend tek panel — full width */}
      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Müşteri Kazanım Trendi</span>
          <span className="c360-chart-panel-sub">Son 12 ay</span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={trendData} margin={{ top: 12, right: 24, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ stroke: '#cbd5e1' }} formatter={(v) => [`${v} yeni müşteri`, 'Adet']} />
            <Area type="monotone" dataKey="count" stroke="#0073ea" fill="rgba(0, 115, 234, 0.15)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Donuts */}
      <div className="c360-chart-donuts">
        <DonutCard title="E-Fatura Adoption" subtitle={`%${eInvoicePct.toFixed(0)} kullanım`} data={eInvoiceData} />
        <DonutCard title="E-İrsaliye Adoption" subtitle={`%${eDispatchPct.toFixed(0)} kullanım`} data={eDispatchData} />
        <DonutCard title="Vergi Tipi" subtitle={`${taxData.length} kategori`} data={taxData} />
      </div>
    </div>
  );
}

const IMPORT_COLUMN_KEYS = {
  textId: ['Müşteri Kodu', 'Musteri Kodu', 'textId', 'TextId', 'Kod'],
  title: ['Ünvan', 'Unvan', 'title', 'Title'],
  shortName: ['Kısa Ad', 'Kisa Ad', 'shortName', 'ShortName'],
  person: ['Yetkili', 'person', 'Person'],
  phone: ['Telefon', 'phone', 'Phone'],
  email: ['E-Posta', 'E-posta', 'Eposta', 'email', 'Email'],
  city: ['İl', 'Il', 'Şehir', 'Sehir', 'city', 'City'],
  province: ['İlçe', 'Ilce', 'province', 'Province', 'District'],
  country: ['Ülke', 'Ulke', 'country', 'Country'],
  taxNumber: ['Vergi No', 'taxNumber', 'TaxNumber', 'VKN'],
  taxOffice: ['Vergi Dairesi', 'taxOffice', 'TaxOffice'],
  adress: ['Adres', 'adress', 'Adress'],
  // #752 Müşteri Excel şablonuna eklenen alanlar (entity'de zaten mevcut kolonlar).
  neighborhood: ['Mahalle', 'neighborhood', 'Neighborhood'],
  street: ['Sokak', 'street', 'Street'],
  apartmentNo: ['Kapı No', 'Kapi No', 'Kapı Numarası', 'Kapi Numarasi', 'apartmentNo', 'ApartmentNo'],
  identityNumber: ['TC Kimlik No', 'TC Kimlik', 'TCKN', 'TC', 'identityNumber', 'IdentityNumber'],
  creditLimit1: ['Kredi Limit 1', 'Kredi Limiti 1', 'creditLimit1', 'CreditLimit1'],
  creditLimit2: ['Kredi Limit 2', 'Kredi Limiti 2', 'creditLimit2', 'CreditLimit2'],
  dueDate: ['Vade Günü', 'Vade Gunu', 'Vade Gün', 'Vade', 'dueDate', 'DueDate'],
  groupCode: ['Grup Kodu', 'Grup Kod', 'Müşteri Grubu Kodu', 'Musteri Grubu Kodu', 'groupCode', 'GroupCode'],
  groupName: ['Grup Adı', 'Grup Adi', 'Müşteri Grubu', 'Musteri Grubu', 'Grup', 'groupName', 'GroupName', 'Group'],
  subGroupCode: ['Ek Grup Kodu', 'Alt Grup Kodu', 'subGroupCode', 'SubGroupCode'],
  subGroupName: ['Ek Grup Adı', 'Ek Grup Adi', 'Alt Grup Adı', 'Ek Grup', 'subGroupName', 'SubGroupName'],
  // #819 Müşteri formunda tek alan: "Distribütör" = vendorId (Vendor entity).
  // #819 Görünen ad "Distribütör"; eski başlıklı dosyalar bozulmasın diye legacy
  // takma adlar (Tedarikçi / Dağıtım Kanalı) listede korunur.
  vendorCode: ['Distribütör Kodu', 'Distributor Kodu', 'Tedarikçi Kodu', 'Tedarikci Kodu', 'Dağıtım Kanalı Kodu', 'Dagitim Kanali Kodu', 'vendorCode', 'VendorCode'],
  vendorName: ['Distribütör Adı', 'Distribütör', 'Distributor', 'Tedarikçi Adı', 'Tedarikçi', 'Tedarikci', 'Dağıtım Kanalı', 'Dagitim Kanali', 'vendorName', 'Vendor'],
  repCode: ['Temsilci Kodu', 'Satış Temsilcisi Kodu', 'Satis Temsilcisi Kodu', 'repCode', 'RepCode'],
  repName: ['Temsilci Adı', 'Temsilci', 'Satış Temsilcisi', 'Satis Temsilcisi', 'repName', 'RepName'],
};

// İçe aktarımda Grup/Ek Grup/Distribütör ilişkilerini ad veya kodla çözer; sistemde yoksa oluşturur.
const CUST_REL_META = {
  group: { base: '/api/customergroups', label: 'Grup', nameOf: (it) => it?.description || it?.name, makeBody: (textId, name) => ({ textId, description: name }) },
  subGroup: { base: '/api/customersubgroups', label: 'Ek Grup', nameOf: (it) => it?.description || it?.name, makeBody: (textId, name) => ({ textId, description: name }) },
  vendor: { base: '/api/vendors', label: 'Distribütör', nameOf: (it) => it?.name, makeBody: (textId, name) => ({ textId, name, city: '', region1: '', region2: '', status: 0 }) },
  // Temsilci yalnız eşleştirilir, otomatik OLUŞTURULMAZ (zorunlu VendorId/SalesManager FK'leri var).
  representative: { base: '/api/representatives', label: 'Temsilci', nameOf: (it) => it?.name, makeBody: null },
};
const normRel = (v) => String(v ?? '').trim().toLocaleLowerCase('tr');
const genRelCode = (name) => {
  const base = String(name || '').toLocaleUpperCase('tr')
    .replace(/İ/g, 'I').replace(/Ş/g, 'S').replace(/Ğ/g, 'G')
    .replace(/Ü/g, 'U').replace(/Ö/g, 'O').replace(/Ç/g, 'C')
    .replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return base || 'GRUP';
};
async function loadCustRelCache() {
  const headers = authHeaders();
  const mk = (items, getName) => {
    const byCode = new Map();
    const byName = new Map();
    const usedCodes = new Set();
    (items || []).forEach((it) => {
      if (it?.textId) { byCode.set(normRel(it.textId), it.id); usedCodes.add(normRel(it.textId)); }
      const nm = getName(it);
      if (nm) byName.set(normRel(nm), it.id);
    });
    return { byCode, byName, usedCodes };
  };
  const [g, sg, v, rep] = await Promise.all([
    fetch('/api/customergroups/Select', { headers }).then((r) => r.ok ? r.json() : []),
    fetch('/api/customersubgroups/Select', { headers }).then((r) => r.ok ? r.json() : []),
    fetch('/api/vendors/Select', { headers }).then((r) => r.ok ? r.json() : []),
    fetch('/api/representatives/Select', { headers }).then((r) => r.ok ? r.json() : []),
  ]);
  return {
    group: mk(unwrapLookupItems(g), (it) => it.description || it.name),
    subGroup: mk(unwrapLookupItems(sg), (it) => it.description || it.name),
    vendor: mk(unwrapLookupItems(v), (it) => it.name),
    representative: mk(unwrapLookupItems(rep), (it) => it.name),
  };
}
async function resolveCustRelation(cache, type, rawCode, rawName) {
  const code = String(rawCode ?? '').trim();
  const name = String(rawName ?? '').trim();
  if (!code && !name) return null;
  const c = cache[type];
  if (code && c.byCode.has(normRel(code))) return c.byCode.get(normRel(code));
  if (name && c.byName.has(normRel(name))) return c.byName.get(normRel(name));
  const meta = CUST_REL_META[type];
  // makeBody yoksa (örn. Temsilci) otomatik oluşturma kapalı — bulunamadıysa null.
  if (!meta.makeBody) return null;
  // Önbellek atlamış olabilir; YARATMADAN ÖNCE sunucudan kodla doğrula (kopya kod yaratma guard'ı).
  if (code) {
    try {
      const url = `${meta.base}?$filter=TextId eq '${code.replace(/'/g, "''")}'&$top=1`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        const hit = unwrapLookupItems(await r.json())[0];
        if (hit && hit.id) {
          c.byCode.set(normRel(code), hit.id);
          c.usedCodes.add(normRel(code));
          const hn = meta.nameOf(hit);
          if (hn) c.byName.set(normRel(hn), hit.id);
          return hit.id;
        }
      }
    } catch { /* doğrulama başarısızsa aşağıdaki oluşturma akışına düş */ }
  }
  let textId = code || genRelCode(name);
  if (c.usedCodes.has(normRel(textId))) {
    let n = 2;
    while (c.usedCodes.has(normRel(`${textId}_${n}`))) n += 1;
    textId = `${textId}_${n}`;
  }
  const res = await fetch(`${meta.base}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(meta.makeBody(textId, name || code)) });
  if (!res.ok) throw new Error(`${meta.label} "${name || code}" oluşturulamadı (${res.status})`);
  const listRes = await fetch(`${meta.base}/Select`, { headers: authHeaders() });
  const items = unwrapLookupItems(listRes.ok ? await listRes.json() : []);
  const found = items.find((it) => normRel(it.textId) === normRel(textId));
  if (!found) throw new Error(`${meta.label} "${name || code}" oluşturuldu ama bulunamadı`);
  c.byCode.set(normRel(textId), found.id);
  if (name) c.byName.set(normRel(name), found.id);
  c.usedCodes.add(normRel(textId));
  return found.id;
}
// Müşteri Kodu (TextId) sistemde var mı? OData ile bulur (önbellekli). Varsa mevcut kaydı döner.
async function findCustomerByCode(apiBase, codeCache, textId) {
  const code = String(textId ?? '').trim();
  if (!code) return null;
  const key = normRel(code);
  if (codeCache.has(key)) return codeCache.get(key);
  const url = `${apiBase}?$filter=TextId eq '${code.replace(/'/g, "''")}'&$top=1`;
  const res = await fetch(url, { headers: authHeaders() });
  let existing = null;
  if (res.ok) {
    const data = await res.json();
    existing = unwrapLookupItems(data)[0] || null;
  }
  codeCache.set(key, existing);
  return existing;
}

function pickField(row, keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && row[k] !== '') return String(row[k]).trim();
  }
  return '';
}

function rowToCustomerPayload(row) {
  const out = {};
  for (const [field, keys] of Object.entries(IMPORT_COLUMN_KEYS)) {
    out[field] = pickField(row, keys);
  }
  return out;
}

function validateImportRow(payload) {
  if (!payload.textId) return 'Müşteri Kodu boş';
  if (!payload.title) return 'Ünvan boş';
  return null;
}

function ImportWizard({ open, onClose, onComplete, apiBase, downloadTemplate }) {
  const [stage, setStage] = useState('pick');
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parsedPayloads, setParsedPayloads] = useState([]);
  const [validation, setValidation] = useState({ valid: 0, invalid: 0 });
  const [progress, setProgress] = useState({ current: 0, total: 0, ok: 0, fail: 0 });
  const [errors, setErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStage('pick'); setFile(null); setRows([]); setParsedPayloads([]);
      setValidation({ valid: 0, invalid: 0 });
      setProgress({ current: 0, total: 0, ok: 0, fail: 0 });
      setErrors([]);
      setWorkbook(null); setSheetNames([]); setSelectedSheet('');
    }
  }, [open]);

  if (!open) return null;

  // Seçilen sheet'i parse edip önizlemeye geç.
  const parseSheet = (wb, name) => {
    const sheet = wb.Sheets[name];
    const data = sheet ? XLSX.utils.sheet_to_json(sheet, { defval: '' }) : [];
    const payloads = data.map(rowToCustomerPayload);
    const v = payloads.reduce((acc, p) => {
      if (validateImportRow(p)) acc.invalid += 1; else acc.valid += 1;
      return acc;
    }, { valid: 0, invalid: 0 });
    setRows(data);
    setParsedPayloads(payloads);
    setValidation(v);
    setStage('preview');
  };

  const sheetRowCount = (wb, name) => {
    const sheet = wb?.Sheets?.[name];
    return sheet ? XLSX.utils.sheet_to_json(sheet, { defval: '' }).length : 0;
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const names = wb.SheetNames || [];
      setWorkbook(wb);
      setSheetNames(names);
      // İçinde veri olan ilk sheet'i öner (boş ilk sheet'i atla — "yan sheet" sorunu).
      const firstWithData = names.find((n) => sheetRowCount(wb, n) > 0) || names[0] || '';
      setSelectedSheet(firstWithData);
      // Birden çok sheet varsa kullanıcı seçsin; tek sheet ise direkt önizleme.
      if (names.length > 1) setStage('sheet');
      else parseSheet(wb, firstWithData);
    } catch (e) {
      setErrors([{ row: 0, message: 'Dosya okunamadı: ' + e.message }]);
      setStage('done');
    }
  };

  const startImport = async () => {
    setStage('importing');
    setProgress({ current: 0, total: parsedPayloads.length, ok: 0, fail: 0 });
    setErrors([]);
    const errs = [];
    let ok = 0;
    let fail = 0;
    let relCache;
    try {
      relCache = await loadCustRelCache();
    } catch (e) {
      // İlişki önbelleği (Grup/Ek Grup/Distribütör) yüklenemezse İÇE AKTARMA İPTAL.
      // Boş önbellekle devam edilirse her satır için mevcut grup yeniden YARATILIR
      // (ör. "001" kodlu yüzlerce kopya grup) — bu yüzden sessizce devam etmek YASAK.
      setErrors([{ row: 0, message: 'Grup/Distribütör listeleri yüklenemedi, içe aktarma iptal edildi: ' + (e?.message || e) }]);
      setStage('done');
      return;
    }
    const codeCache = new Map();
    for (let i = 0; i < parsedPayloads.length; i += 1) {
      const p = parsedPayloads[i];
      const validationErr = validateImportRow(p);
      if (validationErr) {
        fail += 1;
        errs.push({ row: i + 2, message: validationErr });
      } else {
        try {
          const { groupCode, groupName, subGroupCode, subGroupName, vendorCode, vendorName, repCode, repName, ...rest } = p;
          const groupId = await resolveCustRelation(relCache, 'group', groupCode, groupName);
          const subGroupId = await resolveCustRelation(relCache, 'subGroup', subGroupCode, subGroupName);
          const vendorId = await resolveCustRelation(relCache, 'vendor', vendorCode, vendorName);
          const salesRepresentativeId = await resolveCustRelation(relCache, 'representative', repCode, repName);
          // Excel'de dolu gelen alanlar (boşlar mevcut değeri ezmesin)
          const incoming = Object.fromEntries(Object.entries(rest).filter(([, val]) => val !== '' && val != null));
          // #752 Sayısal alanlar backend'de decimal?/int? — string gönderilirse JSON deserialize kırılır.
          // Dolu gelen sayısalları number'a çevir; parse edilemeyeni gönderme (mevcut değeri ezmesin).
          for (const [k, isInt] of [['creditLimit1', false], ['creditLimit2', false], ['dueDate', true]]) {
            if (incoming[k] === undefined) continue;
            let raw = String(incoming[k]).trim();
            // Virgül varsa Türkçe format (nokta = binlik, virgül = ondalık); yoksa nokta ondalık kalır.
            if (raw.includes(',')) raw = raw.replace(/\./g, '').replace(',', '.');
            raw = raw.replace(/[^0-9.-]/g, '');
            const num = isInt ? parseInt(raw, 10) : Number(raw);
            if (Number.isFinite(num)) incoming[k] = num; else delete incoming[k];
          }

          const existing = await findCustomerByCode(apiBase, codeCache, rest.textId);
          let res;
          if (existing && existing.id) {
            // GÜNCELLE — mevcut kaydı koru, gelen dolu alanları + çözülen ilişkileri üzerine yaz
            const merged = { ...existing, ...incoming, id: existing.id };
            if (groupId) merged.groupId = groupId;
            if (subGroupId) merged.subGroupId = subGroupId;
            if (vendorId) merged.vendorId = vendorId;
            if (salesRepresentativeId) merged.salesRepresentativeId = salesRepresentativeId;
            res = await fetch(`${apiBase}/Update`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(merged) });
            if (res.ok) codeCache.set(normRel(rest.textId), merged); // sonraki aynı kod tekrar güncellesin
          } else {
            // YENİ KAYIT
            const payload = {
              ...incoming,
              groupId, subGroupId, vendorId, salesRepresentativeId,
              status: 0, taxType: null, currentBalance: null,
            };
            res = await fetch(`${apiBase}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
          }
          if (res.ok) {
            ok += 1;
          } else {
            fail += 1;
            errs.push({ row: i + 2, message: `Sunucu ${res.status}` });
          }
        } catch (e) {
          fail += 1;
          errs.push({ row: i + 2, message: e.message || 'Network hatası' });
        }
      }
      setProgress({ current: i + 1, total: parsedPayloads.length, ok, fail });
      if (errs.length !== errors.length) setErrors([...errs].slice(0, 50));
    }
    setErrors(errs.slice(0, 50));
    setStage('done');
  };

  const handleFinish = () => {
    onComplete?.(progress.ok);
    onClose();
  };

  const previewRows = rows.slice(0, 5);
  const previewKeys = previewRows.length > 0 ? Object.keys(previewRows[0]) : [];
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>Müşteri İçe Aktar</h3>
          <button type="button" style={s.closeBtn} onClick={onClose}>x</button>
        </div>
        <div style={s.modalBody}>
          <div className="cd-import">
            <div className="cd-import-stepbar">
              <span className={`cd-import-step ${stage === 'pick' || stage === 'sheet' ? 'is-active' : 'is-done'}`}>1. Dosya</span>
              <span className="cd-import-step-sep">›</span>
              <span className={`cd-import-step ${stage === 'preview' ? 'is-active' : (stage === 'importing' || stage === 'done' ? 'is-done' : '')}`}>2. Önizleme</span>
              <span className="cd-import-step-sep">›</span>
              <span className={`cd-import-step ${stage === 'importing' ? 'is-active' : (stage === 'done' ? 'is-done' : '')}`}>3. Yükleme</span>
              <span className="cd-import-step-sep">›</span>
              <span className={`cd-import-step ${stage === 'done' ? 'is-active' : ''}`}>4. Sonuç</span>
            </div>

            {stage === 'pick' && (
              <>
                <div
                  className={`cd-import-drop${dragActive ? ' is-active' : ''}`}
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0]); }}
                >
                  <div className="cd-import-drop-icon"><UploadIcon size={24} /></div>
                  <div className="cd-import-drop-title">Dosyayı buraya sürükleyin ya da tıklayın</div>
                  <div className="cd-import-drop-sub">Desteklenen formatlar: .xlsx, .xls, .csv</div>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }}
                />
                <button type="button" className="cd-import-template" onClick={downloadTemplate}>
                  <ExcelIcon size={14} /> Şablonu İndir
                </button>
                <div className="cd-import-help">
                  Şablondaki kolon başlıklarını koruyun. Zorunlu alanlar: <strong>Müşteri Kodu</strong> ve <strong>Ünvan</strong>.
                  Grup / Ek Grup / Distribütör <strong>önce koddan</strong> eşlenir; sistemde yoksa yeni kayıt oluşturulur.
                  Temsilci yalnız koddan/addan eşlenir (yoksa boş geçilir). <strong>Müşteri Kodu sistemde varsa kayıt güncellenir</strong>
                  (boş bıraktığınız alanlar değişmez), yoksa yeni eklenir.
                </div>
              </>
            )}

            {stage === 'sheet' && (
              <>
                <div className="cd-import-help">
                  Dosyada birden çok sayfa (sheet) var. İçe aktarılacak sayfayı seçin —
                  verileriniz farklı bir sayfadaysa burada seçebilirsiniz.
                </div>
                <div className="cd-import-sheets">
                  {sheetNames.map((n) => {
                    const count = sheetRowCount(workbook, n);
                    return (
                      <label key={n} className={`cd-import-sheet${selectedSheet === n ? ' is-active' : ''}`}>
                        <input type="radio" name="cd-sheet" checked={selectedSheet === n} onChange={() => setSelectedSheet(n)} />
                        <span className="cd-import-sheet-name">{n}</span>
                        <span className="cd-import-sheet-count">{count} satır</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {stage === 'preview' && (
              <>
                <div className="cd-import-summary">
                  <div className="cd-import-stat cd-import-stat--info">
                    <div className="cd-import-stat-label">Toplam Satır</div>
                    <div className="cd-import-stat-value">{rows.length}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--ok">
                    <div className="cd-import-stat-label">Geçerli</div>
                    <div className="cd-import-stat-value">{validation.valid}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--fail">
                    <div className="cd-import-stat-label">Hatalı</div>
                    <div className="cd-import-stat-value">{validation.invalid}</div>
                  </div>
                </div>
                {validation.invalid > 0 && (
                  <div className="cd-import-warn">
                    {validation.invalid} satırda zorunlu alan (Müşteri Kodu veya Ünvan) boş. Bu satırlar atlanacak.
                  </div>
                )}
                <div className="cd-import-preview">
                  <table>
                    <thead>
                      <tr>{previewKeys.map((k) => <th key={k}>{k}</th>)}</tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, i) => {
                        const invalid = validateImportRow(parsedPayloads[i]);
                        return (
                          <tr key={i} className={invalid ? 'is-invalid' : ''}>
                            {previewKeys.map((k) => <td key={k} title={String(r[k] ?? '')}>{String(r[k] ?? '')}</td>)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700 }}>
                  Dosya: <strong>{file?.name}</strong> · İlk 5 satır önizleniyor
                </div>
              </>
            )}

            {stage === 'importing' && (
              <>
                <div className="cd-import-progress">
                  <div className="cd-import-progress-bar">
                    <div className="cd-import-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="cd-import-progress-text">{progress.current} / {progress.total} · %{pct}</div>
                </div>
                <div className="cd-import-summary">
                  <div className="cd-import-stat cd-import-stat--ok">
                    <div className="cd-import-stat-label">Eklenen</div>
                    <div className="cd-import-stat-value">{progress.ok}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--fail">
                    <div className="cd-import-stat-label">Başarısız</div>
                    <div className="cd-import-stat-value">{progress.fail}</div>
                  </div>
                </div>
              </>
            )}

            {stage === 'done' && (
              <>
                <div className="cd-import-summary">
                  <div className="cd-import-stat cd-import-stat--ok">
                    <div className="cd-import-stat-label">Eklendi</div>
                    <div className="cd-import-stat-value">{progress.ok}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--fail">
                    <div className="cd-import-stat-label">Başarısız</div>
                    <div className="cd-import-stat-value">{progress.fail}</div>
                  </div>
                  <div className="cd-import-stat cd-import-stat--info">
                    <div className="cd-import-stat-label">Toplam</div>
                    <div className="cd-import-stat-value">{progress.total}</div>
                  </div>
                </div>
                {errors.length > 0 && (
                  <div className="cd-import-errors">
                    <div className="cd-import-errors-title">Hatalar ({errors.length}{errors.length === 50 ? '+' : ''})</div>
                    {errors.map((e, i) => (
                      <div key={i} className="cd-import-error">
                        <strong>Satır {e.row}</strong>
                        <span>{e.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div style={s.modalFooter}>
          {stage === 'pick' && (
            <button type="button" style={s.btnSecondary} onClick={onClose}>İptal</button>
          )}
          {stage === 'sheet' && (
            <>
              <button type="button" style={s.btnSecondary} onClick={() => setStage('pick')}>Geri</button>
              <button type="button" style={s.btnPrimary} disabled={!selectedSheet} onClick={() => parseSheet(workbook, selectedSheet)}>
                Devam
              </button>
            </>
          )}
          {stage === 'preview' && (
            <>
              <button type="button" style={s.btnSecondary} onClick={() => setStage(sheetNames.length > 1 ? 'sheet' : 'pick')}>Geri</button>
              <button type="button" style={s.btnPrimary} disabled={validation.valid === 0} onClick={startImport}>
                {validation.valid} kayıt yükle
              </button>
            </>
          )}
          {stage === 'importing' && (
            <button type="button" style={s.btnSecondary} disabled>Yükleniyor...</button>
          )}
          {stage === 'done' && (
            <button type="button" style={s.btnPrimary} onClick={handleFinish}>Bitir</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CustomersPage({ definition = 'customers' } = {}) {
  usePageMeta('Müşteriler', 'Müşteri kayıtlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  // Sekme rota tabanlı: hangi rotada açıldıysa o sekme aktif. Prop DOĞRUDAN okunur, state'e
  // KOPYALANMAZ — üç rota da (/customers, /customer-groups, /customer-sub-groups) aynı bileşen
  // tipini ağacın aynı yerinde çizdiği için React Router bunu remount ETMEZ, yalnız prop'ları
  // günceller. `useState(initialDefinition)` prop'u ilk mount'ta dondurduğundan sekmeye basmak
  // URL'i değiştiriyor ama ekranı değiştirmiyordu.
  const activeDefinition = definition;
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [groupBy, setGroupBy] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [bucketLoadingByPath, setBucketLoadingByPath] = useState({});
  const [fetchedBucketPaths, setFetchedBucketPaths] = useState(() => new Set());
  const FULL_FETCH_PAGE_SIZE = 500;
  const effectiveGroupByList = useMemo(
    () => ((groupBy.length === 0 && viewMode === 'kanban') ? ['status'] : groupBy),
    [groupBy, viewMode],
  );
  const effectiveGroupBy = effectiveGroupByList[0] || 'none';  // tek-field kullanan kanban/count için
  const isGrouping = effectiveGroupByList.length > 0;
  const needsFullData = isGrouping || viewMode === 'kanban' || viewMode === 'chart';
  const effectivePageSize = needsFullData ? FULL_FETCH_PAGE_SIZE : pageSize;
  const effectiveCurrentPage = needsFullData ? 1 : currentPage;

  // $select: düz liste görünümünde yalnız GÖRÜNÜR kolonların OData kaynak alanlarını çekeriz
  // (65 kolon yerine ~9) — Column Chooser ile entegre. COLUMN_FILTER_META her kolon id'sinin
  // gerçek OData alanını verir (ör. groupLabel→GroupId). 'Id' her zaman gerekli (satır anahtarı
  // + görüntüle/değiştir/sil navigasyonu). Grup/kanban/chart tam veri istediğinden null döner.
  const listSelectFields = useMemo(() => {
    if (needsFullData) return null;
    // 'Id' satır anahtarı; 'SeasonalInfo' (#240) grid kolonu değil ama Düzenle ekranına
    // state.row ile taşınır — projeksiyondan düşerse kayıtta null'lanıp sezon bilgisi silinir.
    const set = new Set(['Id', 'SeasonalInfo']);
    for (const [colId, meta] of Object.entries(COLUMN_FILTER_META)) {
      if (meta.odataField && columnVisibility[colId] !== false) set.add(meta.odataField);
    }
    if (sortField) set.add(sortField); // sıralanan alan (görünmese de gerekebilir)
    return Array.from(set);
  }, [needsFullData, columnVisibility, sortField]);

  const toggleGroupExpand = (path) => {
    const pathStr = String(path);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      const wasExpanded = next.has(pathStr);
      if (wasExpanded) next.delete(pathStr); else next.add(pathStr);
      return next;
    });
    // Sadece leaf bucket genişlediğinde row fetch tetiklenir; count'lar zaten $apply ile geldi.
    if (!expandedGroups.has(pathStr)) {
      const depth = pathStr.split('::').length;
      const isLeaf = depth >= effectiveGroupByList.length;
      if (isLeaf && !fetchedBucketPaths.has(pathStr)) {
        fetchBucketLeafRows(pathStr);
      }
    }
  };
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  // Lookup'lar yüklenmeden fetchData çalışırsa Grup/EkGrup/Distribütör kolonları boş gelir.
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getGroupLabel = useCallback((id) => groups.find(item => item.id === id)?.description || groups.find(item => item.id === id)?.textId || '-', [groups]);
  const getSubGroupLabel = useCallback((id) => subGroups.find(item => item.id === id)?.description || subGroups.find(item => item.id === id)?.textId || '-', [subGroups]);
  const getVendorLabel = useCallback((id) => {
    const vendor = vendors.find(item => item.id === id);
    return vendor?.name ? `${vendor.textId || ''} - ${vendor.name}`.trim().replace(/^- /, '') : vendor?.textId || '-';
  }, [vendors]);

  // Export için ham KOD ve AD (etiket/birleşik değil) — kod+ad ayrı kolonlar.
  const getGroupCode = useCallback((id) => groups.find(item => item.id === id)?.textId || '', [groups]);
  const getGroupName = useCallback((id) => { const it = groups.find(x => x.id === id); return it ? (it.description || it.name || '') : ''; }, [groups]);
  const getSubGroupCode = useCallback((id) => subGroups.find(item => item.id === id)?.textId || '', [subGroups]);
  const getSubGroupName = useCallback((id) => { const it = subGroups.find(x => x.id === id); return it ? (it.description || it.name || '') : ''; }, [subGroups]);
  const getVendorCode = useCallback((id) => vendors.find(item => item.id === id)?.textId || '', [vendors]);
  const getVendorName = useCallback((id) => vendors.find(item => item.id === id)?.name || '', [vendors]);
  const getRepCode = useCallback((id) => representatives.find(item => item.id === id)?.textId || '', [representatives]);
  const getRepName = useCallback((id) => representatives.find(item => item.id === id)?.name || '', [representatives]);

  const loadLookups = useCallback(() => {
    const headers = authHeaders();
    return Promise.all([
      fetch('/api/customergroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/customersubgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/vendors/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/representatives/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([groupData, subGroupData, vendorData, repData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
      setVendors(unwrapLookupItems(vendorData));
      setRepresentatives(unwrapLookupItems(repData));
    }).catch(() => {}).finally(() => setLookupsLoaded(true));
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);

  const fetchData = useCallback(async () => {
    if (!lookupsLoaded) return; // lookuplar bitmeden çek → Grup/EkGrup kolonları boş gelir
    setLoading(true);
    try {
      const clauses = [];
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        clauses.push(`(contains(tolower(TextId),'${q}') or contains(tolower(Title),'${q}') or contains(tolower(ShortName),'${q}') or contains(tolower(Person),'${q}'))`);
      }
      for (const [field, filter] of Object.entries(columnFilters)) {
        const meta = COLUMN_FILTER_META[field];
        const clause = buildFilterClause(meta, filter);
        if (clause) clauses.push(clause);
      }
      const filterParams = clauses.join(' and ');
      const url = buildODataUrl(API_BASE, { pageSize: effectivePageSize, currentPage: effectiveCurrentPage, sortField, sortDir, filterParams, select: listSelectFields });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(await withComputedBalances(pageRows));
      setTotalCount(await resolveODataTotalCount({ data, rows: pageRows, currentPage: effectiveCurrentPage, pageSize: effectivePageSize, base: API_BASE, filterParams, headers: authHeaders() }));
    } catch (error) {
      showToast('error', error.message || 'Veri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [filterText, columnFilters, sortField, sortDir, effectivePageSize, effectiveCurrentPage, lookupsLoaded, listSelectFields]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Create/Edit artık tam-sayfa standart ekranda (CustomerEditPage / DetailShell) açılır.
  const openNew = () => navigate('/customers/new');
  const openEdit = (row = selected) => { if (!row?.id) return; navigate(`/customers/${row.id}/edit`, { state: { row } }); };
  const openView = (row = selected) => { if (!row?.id) return; navigate(`/customers/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Müşteri başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const lookupOptions = useMemo(() => ({
    groupLabel: groups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
    subGroupLabel: subGroups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
    vendorLabel: vendors.map((v) => ({ value: v.id, label: v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id) })),
  }), [groups, subGroups, vendors]);

  // labelMaps + getKeyLabel: tek satırın belirli bir field'a göre key/label türetir.
  const groupingHelpers = useMemo(() => {
    const labelMaps = {
      groupId: new Map(groups.map((g) => [g.id, g.description || g.textId || g.id])),
      subGroupId: new Map(subGroups.map((g) => [g.id, g.description || g.textId || g.id])),
      vendorId: new Map(vendors.map((v) => [v.id, v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id)])),
    };
    const getKeyLabel = (field, row) => {
      let rawKey = row[field];
      if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
      const key = field === 'status' ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
      let label;
      if (key === '__none__') label = 'Atanmamış';
      else if (field === 'status') label = STATUS_META[key]?.label || String(rawKey);
      else if (field === 'groupId') label = labelMaps.groupId.get(key) || '-';
      else if (field === 'subGroupId') label = labelMaps.subGroupId.get(key) || '-';
      else if (field === 'vendorId') label = labelMaps.vendorId.get(key) || '-';
      else label = String(key);
      return { key, label };
    };
    return { labelMaps, getKeyLabel };
  }, [groups, subGroups, vendors]);

  // Tek-field flat bucket'lar (kanban için kullanılır).
  const groupBuckets = useMemo(() => {
    if (!isGrouping) return [];
    const { labelMaps, getKeyLabel } = groupingHelpers;
    const groupField = effectiveGroupBy;
    const buckets = new Map();
    const ensure = (key, label) => {
      if (!buckets.has(key)) buckets.set(key, { key, label, rows: [] });
      return buckets.get(key);
    };
    if (groupField === 'status') Object.entries(STATUS_META).forEach(([k, meta]) => ensure(Number(k), meta.label));
    else if (groupField === 'groupId') groups.forEach((g) => ensure(g.id, labelMaps.groupId.get(g.id)));
    else if (groupField === 'subGroupId') subGroups.forEach((g) => ensure(g.id, labelMaps.subGroupId.get(g.id)));
    else if (groupField === 'vendorId') vendors.forEach((v) => ensure(v.id, labelMaps.vendorId.get(v.id)));
    rows.forEach((row) => {
      const { key, label } = getKeyLabel(groupField, row);
      ensure(key, label).rows.push(row);
    });
    const arr = Array.from(buckets.values());
    arr.sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      return String(a.label).localeCompare(String(b.label), 'tr', { sensitivity: 'base' });
    });
    return arr;
  }, [isGrouping, effectiveGroupBy, rows, groups, subGroups, vendors, groupingHelpers]);

  // Multi-field nested bucket'lar (liste için kullanılır).
  const nestedGroupBuckets = useMemo(() => {
    if (!isGrouping) return [];
    const { getKeyLabel } = groupingHelpers;

    const buildLevel = (rowsAtLevel, depth, parentPath) => {
      if (depth >= effectiveGroupByList.length) return null;
      const field = effectiveGroupByList[depth];
      const map = new Map();
      rowsAtLevel.forEach((row) => {
        const { key, label } = getKeyLabel(field, row);
        const bk = String(key);
        if (!map.has(bk)) {
          const path = parentPath ? `${parentPath}::${bk}` : bk;
          map.set(bk, { key, label, path, rows: [] });
        }
        map.get(bk).rows.push(row);
      });
      const arr = Array.from(map.values()).map((b) => ({
        ...b,
        children: buildLevel(b.rows, depth + 1, b.path),
      }));
      arr.sort((a, b) => {
        if (String(a.key) === '__none__') return 1;
        if (String(b.key) === '__none__') return -1;
        return String(a.label).localeCompare(String(b.label), 'tr', { sensitivity: 'base' });
      });
      return arr;
    };

    return buildLevel(rows, 0, '');
  }, [isGrouping, effectiveGroupByList, rows, groupingHelpers]);
  const columns = useMemo(
    () => [
      columnHelper.accessor('textId', {
        header: 'Müşteri Kodu',
        meta: { filterType: 'text' },
        cell: (info) => {
          const value = info.getValue();
          return value ? <span className="c360-erp-chip">{value}</span> : '-';
        },
      }),
      columnHelper.accessor('title', {
        header: 'Ünvan',
        meta: { filterType: 'text' },
        cell: (info) => {
          const value = info.getValue();
          if (!value) return '-';
          return (
            <span className="c360-customer-cell">
              <AvatarBadge name={value} tone="customer" />
              <span className="c360-cell-main">{value}</span>
            </span>
          );
        },
      }),
      columnHelper.accessor('shortName', {
        header: 'Kısa Ad',
        meta: { filterType: 'text' },
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('person', {
        header: 'Yetkili',
        meta: { filterType: 'text' },
        cell: (info) => {
          const value = info.getValue();
          if (!value) return '-';
          return (
            <span className="c360-customer-cell">
              <AvatarBadge name={value} tone="rep" />
              <span>{value}</span>
            </span>
          );
        },
      }),
      columnHelper.accessor((row) => getGroupLabel(row.groupId), {
        id: 'groupLabel',
        header: 'Müşteri Grubu',
        enableSorting: false,
        meta: { filterType: 'select' },
        cell: (info) => {
          const value = info.getValue();
          if (!value || value === '-') return '-';
          return <PillTag tone="info">{value}</PillTag>;
        },
      }),
      columnHelper.accessor((row) => getSubGroupLabel(row.subGroupId), {
        id: 'subGroupLabel',
        header: 'Ek Grup',
        enableSorting: false,
        meta: { filterType: 'select' },
      }),
      columnHelper.accessor((row) => getVendorLabel(row.vendorId), {
        id: 'vendorLabel',
        header: 'Distribütör',
        enableSorting: false,
        meta: { filterType: 'select' },
      }),
      columnHelper.accessor('status', {
        header: 'Durum',
        meta: { filterType: 'select' },
        cell: (info) => {
          const value = info.getValue();
          if (value === null || value === undefined || value === '') return '-';
          const meta = STATUS_META[value] ?? { label: String(value), tone: 'neutral' };
          return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'İşlemler',
        enableHiding: false,
        cell: ({ row }) => (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(event) => { event.stopPropagation(); openView(row.original); }}>
              <ViewIcon />
            </button>
            {canEdit && (
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(event) => { event.stopPropagation(); openEdit(row.original); }}>
              <EditIcon />
            </button>
            )}
            {canDelete && (
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(event) => { event.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}>
              <DeleteIcon />
            </button>
            )}
          </span>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getGroupLabel, getSubGroupLabel, getVendorLabel, canEdit, canDelete],
  );

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );

  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setCurrentPage(1);
    if (!first) {
      setSortField('TextId');
      setSortDir('asc');
      return;
    }
    setSortField(toODataSortField(first.id));
    setSortDir(first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, rowSelection, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const buildCurrentFilterParams = useCallback(() => {
    const clauses = [];
    if (filterText.trim()) {
      const q = filterText.trim().replace(/'/g, "''").toLowerCase();
      clauses.push(`(contains(tolower(TextId),'${q}') or contains(tolower(Title),'${q}') or contains(tolower(ShortName),'${q}') or contains(tolower(Person),'${q}'))`);
    }
    for (const [field, filter] of Object.entries(columnFilters)) {
      const meta = COLUMN_FILTER_META[field];
      const clause = buildFilterClause(meta, filter);
      if (clause) clauses.push(clause);
    }
    return clauses.join(' and ');
  }, [filterText, columnFilters]);

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const filterParams = buildCurrentFilterParams();
      const url = buildODataUrl(API_BASE, {
        pageSize: 100000, currentPage: 1, sortField, sortDir, filterParams,
      });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const allRows = getODataRows(data);
      if (allRows.length === 0) {
        showToast('error', 'Dışa aktarılacak veri yok');
        return;
      }
      const cols = [
        { field: 'textId', headerName: 'Müşteri Kodu' },
        { field: 'title', headerName: 'Ünvan' },
        { field: 'shortName', headerName: 'Kısa Ad' },
        { field: 'person', headerName: 'Yetkili' },
        { field: 'phone', headerName: 'Telefon' },
        { field: 'email', headerName: 'E-posta' },
        { field: 'city', headerName: 'Şehir' },
        { field: 'taxNumber', headerName: 'Vergi No' },
        { field: 'taxOffice', headerName: 'Vergi Dairesi' },
        { field: 'adress', headerName: 'Adres' },
        // Bağlantılı alanlar: KOD ve AD ayrı kolonlar (içe aktarımda koddan eşleşir).
        { headerName: 'Grup Kodu', valueGetter: ({ row }) => getGroupCode(row.groupId) },
        { headerName: 'Grup Adı', valueGetter: ({ row }) => getGroupName(row.groupId) },
        { headerName: 'Ek Grup Kodu', valueGetter: ({ row }) => getSubGroupCode(row.subGroupId) },
        { headerName: 'Ek Grup Adı', valueGetter: ({ row }) => getSubGroupName(row.subGroupId) },
        // Distribütör = tek alan: vendorId.
        { headerName: 'Distribütör Kodu', valueGetter: ({ row }) => getVendorCode(row.vendorId) },
        { headerName: 'Distribütör Adı', valueGetter: ({ row }) => getVendorName(row.vendorId) },
        { headerName: 'Temsilci Kodu', valueGetter: ({ row }) => getRepCode(row.salesRepresentativeId ?? row.repId) },
        { headerName: 'Temsilci Adı', valueGetter: ({ row }) => getRepName(row.salesRepresentativeId ?? row.repId) },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label ?? row.status ?? '') },
        { field: 'currentBalance', headerName: 'Cari Bakiye' },
        { field: 'creditLimit1', headerName: 'Kredi Limit' },
        { field: 'isEinvoice', headerName: 'E-Fatura' },
        { field: 'isEdispatch', headerName: 'E-İrsaliye' },
        { headerName: 'E-Belge Mükellef Tipi', valueGetter: ({ row }) => EDOCUMENT_TYPE_LABELS[row.eDocumentTaxpayerType] ?? '' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(allRows, cols, `musteriler_${dateStr}`);
      showToast('success', `${allRows.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const [importOpen, setImportOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bucketCounts, setBucketCounts] = useState({});
  const LOAD_MORE_BATCH = 200;

  // OData $apply ile TÜM seviyelerin count'unu TEK request'te çek + parent'lara rollup yap.
  const fetchBucketCounts = useCallback(async () => {
    if (!isGrouping || (viewMode !== 'kanban' && viewMode !== 'list')) {
      setBucketCounts({});
      return;
    }
    const fields = effectiveGroupByList.filter((f) => GROUP_BY_ODATA[f]);
    if (fields.length === 0) { setBucketCounts({}); return; }

    const odataFields = fields.map((f) => GROUP_BY_ODATA[f].field);
    const baseFilter = buildCurrentFilterParams();
    const applyExpr = `groupby((${odataFields.join(',')}),aggregate($count as Count))`;
    let url = `${API_BASE}?$apply=${encodeURIComponent(applyExpr)}`;
    if (baseFilter) url += `&$filter=${encodeURIComponent(baseFilter)}`;
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) { setBucketCounts({}); return; }
      const data = await res.json();
      const rowsAgg = data?.value || [];
      const next = {};
      rowsAgg.forEach((agg) => {
        // Path'i her seviye için oluştur ve rollup yap (leaf count'u tüm ata path'lere ekle).
        const parts = fields.map((f) => {
          const v = agg[GROUP_BY_ODATA[f].field];
          if (v === null || v === undefined) return '__none__';
          return String(v);
        });
        const c = Number(agg.Count) || 0;
        for (let i = 1; i <= parts.length; i += 1) {
          const path = parts.slice(0, i).join('::');
          next[path] = (next[path] || 0) + c;
        }
      });
      setBucketCounts(next);
    } catch {
      setBucketCounts({});
    }
  }, [viewMode, isGrouping, effectiveGroupByList, buildCurrentFilterParams]);

  useEffect(() => { fetchBucketCounts(); }, [fetchBucketCounts]);

  // Bucket path → OData filter clause (örn. "0::Istanbul" + [status, city] → "Status eq 0 and City eq 'Istanbul'")
  const buildPathFilterClause = useCallback((path, fields) => {
    const parts = path.split('::');
    const clauses = [];
    parts.forEach((k, i) => {
      const field = fields[i];
      const meta = GROUP_BY_ODATA[field];
      if (!meta) return;
      if (k === '__none__') {
        clauses.push(meta.type === 'string' ? `(${meta.field} eq null or ${meta.field} eq '')` : `${meta.field} eq null`);
      } else if (meta.type === 'number') {
        clauses.push(`${meta.field} eq ${k}`);
      } else if (meta.type === 'guid') {
        clauses.push(`${meta.field} eq ${k}`);
      } else {
        clauses.push(`${meta.field} eq '${String(k).replace(/'/g, "''")}'`);
      }
    });
    return clauses.join(' and ');
  }, []);

  // Bir bucket'ı genişletince çocukları için count fetch (non-leaf level).
  // Leaf bucket için backend'den rows çek + ana rows state'ine dedupe ile ekle.
  const fetchBucketLeafRows = useCallback(async (path) => {
    if (fetchedBucketPaths.has(path)) return;
    const baseFilter = buildCurrentFilterParams();
    const bucketFilter = buildPathFilterClause(path, effectiveGroupByList);
    const parts = [];
    if (baseFilter) parts.push(`(${baseFilter})`);
    if (bucketFilter) parts.push(bucketFilter);
    const combined = parts.join(' and ');

    setBucketLoadingByPath((prev) => ({ ...prev, [path]: true }));
    try {
      const url = `${API_BASE}?$count=true&$top=500&$skip=0&$orderby=${encodeURIComponent(`${sortField} ${sortDir}`)}${combined ? `&$filter=${encodeURIComponent(combined)}` : ''}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const fetchedRows = getODataRows(data);
      const c = getExplicitODataTotalCount(data);
      if (c != null) setBucketCounts((prev) => ({ ...prev, [path]: c }));
      const withBal = await withComputedBalances(fetchedRows);
      setRows((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newRows = withBal.filter((r) => !existingIds.has(r.id));
        return newRows.length ? [...prev, ...newRows] : prev;
      });
      setFetchedBucketPaths((prev) => new Set(prev).add(path));
    } catch (e) {
      showToast('error', 'Bucket yüklenemedi: ' + (e.message || ''));
    } finally {
      setBucketLoadingByPath((prev) => { const next = { ...prev }; delete next[path]; return next; });
    }
  }, [effectiveGroupByList, sortField, sortDir, fetchedBucketPaths, buildCurrentFilterParams, buildPathFilterClause]);

  // Reset on filter/grouping/sort değişimi.
  useEffect(() => {
    setExpandedGroups(new Set());
    setFetchedBucketPaths(new Set());
  }, [effectiveGroupByList, filterText, columnFilters, sortField, sortDir]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return;
    if (rows.length >= totalCount) return;
    setLoadingMore(true);
    try {
      const filterParams = buildCurrentFilterParams();
      const skip = rows.length;
      const orderby = encodeURIComponent(`${sortField} ${sortDir}`);
      let url = `${API_BASE}?$count=true&$top=${LOAD_MORE_BATCH}&$skip=${skip}&$orderby=${orderby}`;
      if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
      if (listSelectFields && listSelectFields.length) url += `&$select=${encodeURIComponent(listSelectFields.join(','))}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const newRows = await withComputedBalances(getODataRows(data));
      if (newRows.length > 0) setRows((prev) => [...prev, ...newRows]);
    } catch (e) {
      showToast('error', 'Yükleme hatası: ' + (e.message || ''));
    } finally {
      setLoadingMore(false);
    }
  }, [rows.length, totalCount, sortField, sortDir, loadingMore, loading, buildCurrentFilterParams, listSelectFields]);

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadMore();
    }
  };

  const downloadImportTemplate = () => {
    const sample = [
      {
        'Müşteri Kodu': 'C001',
        'Ünvan': 'Örnek Şirket A.Ş.',
        'Kısa Ad': 'Örnek',
        'Yetkili': 'Ahmet Yılmaz',
        'Telefon': '02121234567',
        'E-posta': 'info@ornek.com',
        'İl': 'İstanbul',
        'İlçe': 'Kadıköy',
        'Ülke': 'Türkiye',
        'Vergi No': '1234567890',
        'Vergi Dairesi': 'Kadıköy',
        'Adres': 'Atatürk Cad. No:1',
        'Mahalle': 'Caferağa',
        'Sokak': 'Moda Cad.',
        'Kapı No': '12',
        'TC Kimlik No': '',
        'Kredi Limit 1': '10000',
        'Kredi Limit 2': '5000',
        'Vade Günü': '30',
        'Grup Kodu': '001',
        'Grup Adı': 'Genel Grup',
        'Ek Grup Kodu': '',
        'Ek Grup Adı': '',
        'Distribütör Kodu': '',
        'Distribütör Adı': '',
        'Temsilci Kodu': '',
        'Temsilci Adı': '',
      },
    ];
    exportToExcel(sample, null, 'musteri_sablon');
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, ünvan veya yetkili ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterText(''); setFilterInput(''); setCurrentPage(1); }}>Temizle</button>
      )}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && (
        <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />
      )}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar"
          aria-label="Excel'e Aktar"
          onClick={handleExport}
        >
          <ExcelIcon size={16} />
        </button>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel/CSV'den İçe Aktar"
          aria-label="Excel/CSV'den İçe Aktar"
          onClick={() => setImportOpen(true)}
        >
          <UploadIcon size={16} />
        </button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const renderDefinitionTabs = () => (
    <div style={customerDefinitionStyles.tabBar}>
      {definitionTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          style={{ ...customerDefinitionStyles.tab, ...(activeDefinition === tab.key ? customerDefinitionStyles.tabActive : {}) }}
          onClick={() => { if (tab.key !== activeDefinition) navigate(tab.route); }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeDefinition === 'groups') return <div style={s.container}>{renderDefinitionTabs()}<CustomerGroupsPage /></div>;
  if (activeDefinition === 'subGroups') return <div style={s.container}>{renderDefinitionTabs()}<CustomerSubGroupsPage /></div>;

  return (
    <div style={s.container}>
      {renderDefinitionTabs()}
      <ListPageShell
        title="Müşteriler"
        subtitle="Müşteri kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={CUSTOMER_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Müşteri</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count" title={bucketCounts[String(bucket.key)] != null ? `${bucket.rows.length} / ${bucketCounts[String(bucket.key)]} yüklü` : `${bucket.rows.length} yüklü`}>
                      {bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}
                    </span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayit yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const rowStatusMeta = STATUS_META[row.status];
                        const isV = (id) => columnVisibility[id] !== false;
                        const groupLbl = getGroupLabel(row.groupId);
                        const subGroupLbl = getSubGroupLabel(row.subGroupId);
                        const vendorLbl = getVendorLabel(row.vendorId);
                        return (
                          <div
                            key={row.id}
                            className="c360-kanban-card"
                            onClick={() => setSelected(row)}
                            onDoubleClick={() => openView(row)}
                          >
                            {isV('title') && (
                              <div className="c360-kanban-card-head">
                                {row.title && <AvatarBadge name={row.title} tone="customer" size={28} />}
                                <span className="c360-kanban-card-title" title={row.title || ''}>{row.title || '-'}</span>
                              </div>
                            )}
                            <div className="c360-kanban-card-meta">
                              {isV('textId') && row.textId && <span className="c360-erp-chip">{row.textId}</span>}
                              {isV('status') && rowStatusMeta && <StatusBadge tone={rowStatusMeta.tone}>{rowStatusMeta.label}</StatusBadge>}
                              {isV('groupLabel') && groupLbl && groupLbl !== '-' && <span className="c360-erp-chip" title="Müşteri Grubu">{groupLbl}</span>}
                              {isV('subGroupLabel') && subGroupLbl && subGroupLbl !== '-' && <span className="c360-erp-chip" title="Ek Grup">{subGroupLbl}</span>}
                              {isV('vendorLabel') && vendorLbl && vendorLbl !== '-' && <span className="c360-erp-chip" title="Distribütör">{vendorLbl}</span>}
                            </div>
                            {isV('shortName') && row.shortName && <div className="c360-kanban-card-sub">{row.shortName}</div>}
                            {isV('person') && row.person && <div className="c360-kanban-card-sub">{row.person}</div>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const meta = STATUS_META[row.status];
              const grp = getGroupLabel(row.groupId);
              return {
                avatarName: row.title,
                avatarTone: 'customer',
                title: row.title,
                subtitle: row.textId,
                statusTone: meta?.tone,
                statusLabel: meta?.label,
                meta: [
                  grp && grp !== '-' ? { k: 'Grup', v: grp } : null,
                  row.person ? { k: 'Yetkili', v: row.person } : null,
                  row.shortName ? { k: 'Kısa Ad', v: row.shortName } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openView },
              { kind: 'edit', title: 'Değiştir', onClick: openEdit, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(true); }, hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openView}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
        <div className="c360-table-wrap">
          <table className="c360-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortState = header.column.getIsSorted();
                    const filterType = header.column.columnDef.meta?.filterType;
                    const filterMeta = COLUMN_FILTER_META[header.column.id];
                    return (
                      <th
                        key={header.id}
                        className={[canSort ? 'is-sortable' : '', header.column.id === 'actions' ? 'c360-act-th' : ''].filter(Boolean).join(' ') || undefined}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        {header.isPlaceholder ? null : (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <span className="c360-sort-icon">{sortState === 'asc' ? '▲' : sortState === 'desc' ? '▼' : ''}</span>
                            )}
                            {filterType && filterMeta && (
                              <ColumnFilterPopover
                                column={header.column}
                                filter={columnFilters[header.column.id]}
                                filterType={filterMeta.type}
                                options={filterMeta.options || lookupOptions[header.column.id]}
                                onApply={(next) => {
                                  if (filterMeta.type === 'select' && (next.value === '' || next.value == null)) {
                                    setColumnFilters((prev) => {
                                      const copy = { ...prev };
                                      delete copy[header.column.id];
                                      return copy;
                                    });
                                  } else {
                                    setColumnFilters((prev) => ({ ...prev, [header.column.id]: next }));
                                  }
                                  setCurrentPage(1);
                                }}
                                onClear={() => {
                                  setColumnFilters((prev) => {
                                    const copy = { ...prev };
                                    delete copy[header.column.id];
                                    return copy;
                                  });
                                  setCurrentPage(1);
                                }}
                              />
                            )}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {(() => {
                const tanstackRows = table.getRowModel().rows;
                if (tanstackRows.length === 0) {
                  return (
                    <tr className="is-empty">
                      <td colSpan={columns.length}>{loading ? 'Yükleniyor...' : 'Kayit bulunamadi'}</td>
                    </tr>
                  );
                }
                const visibleColCount = table.getVisibleLeafColumns().length;
                const renderRow = (trow) => {
                  const isSelectedRow = selected?.id === trow.original.id;
                  return (
                    <tr
                      key={trow.id}
                      className={isSelectedRow ? 'is-selected' : undefined}
                      onClick={() => setSelected(trow.original)}
                      onDoubleClick={() => openView(trow.original)}
                    >
                      {trow.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={cell.column.id === 'actions' ? 'c360-actcell' : undefined}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                };
                if (!isGrouping) {
                  return tanstackRows.map(renderRow);
                }
                const rowById = new Map(tanstackRows.map((r) => [r.original.id, r]));
                const renderNested = (bucketArr, depth) => bucketArr.map((bucket) => {
                  const isExpanded = expandedGroups.has(bucket.path);
                  const lvl = Math.min(depth + 1, 3);
                  const backendCount = bucketCounts[bucket.path];
                  const isLoadingBucket = bucketLoadingByPath[bucket.path];
                  return (
                    <Fragment key={bucket.path}>
                      <tr className={`is-group-header level-${lvl}`} onClick={() => toggleGroupExpand(bucket.path)}>
                        <td colSpan={visibleColCount}>
                          <span style={{ paddingLeft: depth * 20 }}>
                            <span className={`c360-group-chevron${!isExpanded ? ' is-collapsed' : ''}`}>▼</span>
                            <span className="c360-group-label">{bucket.label}</span>
                            <span
                              className="c360-group-count"
                              title={backendCount != null ? `${backendCount} kayıt (backend)` : 'Sayı henüz yüklenmedi'}
                            >
                              {backendCount != null ? backendCount : '—'}
                            </span>
                            {isLoadingBucket && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--accent)' }}>⏳ yükleniyor…</span>}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        bucket.children
                          ? renderNested(bucket.children, depth + 1)
                          : bucket.rows.map((raw) => {
                            const trow = rowById.get(raw.id);
                            return trow ? renderRow(trow) : null;
                          })
                      )}
                    </Fragment>
                  );
                });
                return renderNested(nestedGroupBuckets, 0);
              })()}
            </tbody>
          </table>
          {loading && table.getRowModel().rows.length > 0 && <div style={s.loadingOverlay}>Yükleniyor...</div>}
        </div>
        )}
      </ListPageShell>

      {deleteConfirm && <div style={s.overlay} onClick={() => setDeleteConfirm(false)}><div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}><div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div><div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.title}"</strong> müşterisini silmek istediğinize emin misiniz?</p></div><div style={s.modalFooter}><button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div></div></div>}
      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => {
          if (okCount > 0) {
            showToast('success', `${okCount} müşteri içe aktarıldı`);
            loadLookups();
            fetchData();
          }
        }}
        apiBase={API_BASE}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
